import {
    Box,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Localized } from 'dg-web-shared/lib/Localization';
import { useLoginRequest } from '../../api/ParkingaboLoginApi';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { ParkingaboLogin } from '../AppRoutes';
import { ParkingaboLogoHeader } from 'parkingabo-shared/layout/ParkingaboLogoHeader.tsx';
import { ParkingaboLink } from 'parkingabo-shared/layout/ParkinaboLink.tsx';
import { ParkingaboFooter } from '../../components/layout/ParkingaboFooter';
import { SubmitEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { FeedbackPopup } from '../../components/FeedbackPopup';
import { isFormGlobalViolationEntry } from 'dg-web-shared/lib/forms/FormValidationHelpers';
import { useUnauthenticatedOperator } from '../../components/layout/UnauthenticatedOperatorProvider.tsx';
import { getBridge } from '../../components/Native.tsx';
import {
    devEnvs,
    getDevEnvName,
    getEnvChangeName,
} from 'dg-web-shared/common/utils/DevEnvironments.tsx';
import {
    envIsProduction,
    envIsTest,
    envName,
} from 'dg-web-shared/lib/Environment.ts';

export function LoginRoute({ login }: { login: ParkingaboLogin }) {
    const navigate = useNavigate();
    if (login.loggedIn) {
        navigate(`/`, { replace: true });
        return null;
    }
    return <LoginPage login={login} />;
}

export function OperatorLoginRoute({ login }: { login: ParkingaboLogin }) {
    const navigate = useNavigate();
    const { operatorExternalId, allowSignup } = useUnauthenticatedOperator();
    if (login.loggedIn) {
        navigate(`/`, { replace: true });
        return null;
    }
    return (
        <LoginPage
            login={login}
            tenantAllowsSignup={allowSignup}
            operatorExternalId={operatorExternalId}
        />
    );
}

function LoginPage({
    login,
    tenantAllowsSignup,
    operatorExternalId,
}: {
    login: ParkingaboLogin;
    tenantAllowsSignup?: boolean;
    operatorExternalId?: string;
}) {
    const theme = useTheme();
    const { search } = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(search);
    const [email, setEmail] = useState(
        decodeURIComponent(params.get('email') || ''),
    );
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loginState, sendLogin] = useLoginRequest();
    const [showQRRegistrationInfo, setShowQRRegistrationInfo] = useState(false);
    useServerSuccessEffect(loginState, () => login.refetchLogin());
    useServerErrorEffect(loginState, errStatus => {
        if (errStatus === 403) {
            // use is authenticated but not yet verified
            navigate(`/email-needs-confirmation/${encodeURIComponent(email)}`);
        }
    });
    const envChangeName = getEnvChangeName(email);
    useEffect(() => {
        if (!envChangeName) return;
        if (envChangeName === 'test' && !envIsTest()) {
            window.location.href =
                'https://parkingabo.test.digitalparking.ch/login';
        } else if (
            devEnvs.includes(envChangeName) &&
            envName() !== envChangeName
        ) {
            window.location.href = `https://parkingabo.${envChangeName}.digitalparking.dev/login`;
        } else if (envChangeName === 'prod' && !envIsProduction()) {
            window.location.href = 'https://parkingabo.ch/login';
        }
    }, [envChangeName]);
    const devEnvName = getDevEnvName();

    const hasError = loginState.status === RequestStatus.ERROR;
    const violation = hasError && loginState.data?.violations[0];
    const allowsSignup = tenantAllowsSignup;

    function qrRegistration() {
        const bridge = getBridge();
        if (bridge && bridge.scanQRCode) {
            bridge.scanQRCode();
        } else {
            setShowQRRegistrationInfo(true);
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <ParkingaboLogoHeader />
            <Box
                component="form"
                onSubmit={(e: SubmitEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    sendLogin({
                        email: email,
                        password: password,
                    });
                }}
                sx={{
                    padding: 3,
                }}
            >
                <TextField
                    label={
                        <Localized
                            de="E-Mail"
                            fr="E-mail"
                            it="E-mail"
                            en="Email"
                        />
                    }
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    fullWidth={true}
                    type="email"
                    margin="normal"
                    autoComplete="email"
                />
                <TextField
                    label={
                        <Localized
                            de="Passwort"
                            fr="Mot de passe"
                            it="Password"
                            en="Password"
                        />
                    }
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    fullWidth={true}
                    type="password"
                    autoComplete="current-password"
                    error={hasError}
                    helperText={
                        hasError ? (
                            violation &&
                            isFormGlobalViolationEntry(violation) ? (
                                <Localized
                                    {...violation.issue.humanReadableMessage
                                        .byLanguage}
                                />
                            ) : (
                                <Localized
                                    de="E-Mail und/oder Passwort ungültig."
                                    fr="E-mail et/ou mot de passe invalide."
                                    it="E-mail e/o password non validi."
                                    en="Invalid email and/or password."
                                />
                            )
                        ) : (
                            ' '
                        )
                    }
                    sx={{
                        marginTop: '20px',
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 2,
                        alignItems: 'center',
                    }}
                >
                    <ParkingaboLink
                        to={`/request-password-reset${
                            email.length > 0
                                ? `?email=${encodeURIComponent(email)}`
                                : ''
                        }`}
                        sx={{
                            fontSize: 14,
                            marginRight: 1,
                            fontWeight: 700,
                        }}
                    >
                        <Localized
                            de="Passwort vergessen?"
                            fr="Mot de passe oublié?"
                            it="Password dimenticata?"
                            en="Forgot the password?"
                        />
                    </ParkingaboLink>
                    <FormControlLabel
                        labelPlacement="start"
                        control={<Checkbox color="primary" />}
                        sx={{
                            textAlign: 'right',
                        }}
                        label={
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 14,
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                }}
                            >
                                <Localized
                                    de="Login speichern"
                                    fr="Rester connecté"
                                    it="Rimani connesso"
                                    en="Stay signed in"
                                />
                            </Typography>
                        }
                        checked={rememberMe}
                        onChange={(_, checked) => setRememberMe(checked)}
                    />
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        marginTop: theme.spacing(1),
                        marginBottom: theme.spacing(5),
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={email.length <= 0 || password.length <= 0}
                        sx={{
                            width: '50%',
                            marginLeft: 'auto',
                        }}
                        loading={loginState.status === RequestStatus.PENDING}
                    >
                        <Localized
                            de={`Anmelden${devEnvName}`}
                            fr={`Connexion${devEnvName}`}
                            it={`Accesso${devEnvName}`}
                            en={`Login${devEnvName}`}
                        />
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: '80%',
                        textAlign: 'center',
                        margin: '0 auto',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                        }}
                    >
                        <Localized
                            de="Noch kein Konto?"
                            fr="Pas encore un compte?"
                            it="Non ha ancora un conto?"
                            en="You don't have an account yet?"
                        />
                    </Typography>
                    <ParkingaboLink
                        to={
                            allowsSignup
                                ? `../register${
                                      email.length > 0
                                          ? `?email=${encodeURIComponent(email)}`
                                          : ''
                                  }`
                                : '.'
                        }
                        onClick={!allowsSignup ? qrRegistration : undefined}
                    >
                        <Button variant="outlined" sx={{ marginTop: 1 }}>
                            <Localized
                                de="Hier registrieren"
                                fr="Enregistrez-vous ici"
                                it="Registratevi qui"
                                en="Sign up here"
                            />
                        </Button>
                    </ParkingaboLink>
                </Box>
            </Box>
            <ParkingaboFooter operatorExternalId={operatorExternalId} />
            <FeedbackPopup
                open={showQRRegistrationInfo}
                onConfirm={() => setShowQRRegistrationInfo(false)}
                title={
                    <Localized
                        de="Anleitung"
                        fr="Instructions"
                        it="Istruzioni"
                        en="Instructions"
                    />
                }
                confirmLabel={'OK'}
            >
                <Typography sx={{ fontSize: 14 }}>
                    <Localized
                        de="Um sich zu registrieren, müssen Sie den QR-Code scannen oder die URL-Adresse eingeben, die Sie vom Parking-Betreiber erhalten haben."
                        fr="Pour vous inscrire, vous devez scanner le code QR ou taper l'adresse URL que vous avez reçue de l’exploitant du parking."
                        it="Per procedere alla registrazione dovete scansionare il codice QR o digitare l’indirizzo URL che avete ricevuto dal gestore del parcheggio."
                        en="To sign up, you have to scan the QR code or type in the URL address you received from the car park operator."
                    />
                </Typography>
            </FeedbackPopup>
            <Outlet />
        </Box>
    );
}
